import type { JSX } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { type EnvironmentValue, environmentSchema } from '@majuscule/contracts';
import { z } from 'zod';
import { EnvironmentBadge, ENVIRONMENT_LABELS } from './EnvironmentBadge';
import { useToast } from './toast';
import { internalFetch, internalSend } from '../lib/api';
import { formatEur, initials } from '../lib/format';
import { Callout } from '@/components/ui/alert';
import { CellUser } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHead } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { CheckRow, Field, Fields2, Fieldset, InputGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Pager, usePagination } from '@/components/ui/pager';
import { Tag } from '@/components/ui/tag';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface InternalUser {
  environment: EnvironmentValue;
  externalId: string;
  displayName: string | null;
  active: boolean;
  userCap: string | null;
  canEditOwnCap: boolean;
  canViewAmounts: boolean;
}

// Euro amount or empty string (empty = "use client default" on create, "no cap" on edit).
const EURO_OR_EMPTY = /^(\d+(\.\d{1,6})?)?$/;

const userFormSchema = z.object({
  environment: environmentSchema,
  externalId: z.string().min(1, 'Identifiant requis').max(255),
  displayName: z.string().max(200).optional(),
  userCap: z.string().regex(EURO_OR_EMPTY, 'Montant en euros').optional(),
  canEditOwnCap: z.boolean(),
  canViewAmounts: z.boolean(),
  active: z.boolean(),
});
type FormValues = z.infer<typeof userFormSchema>;

const COLUMN_COUNT = 6;

export function ClientUsers({
  clientId,
  defaultUserCap,
}: {
  clientId: string;
  defaultUserCap: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const base = `/internal/clients/${clientId}/users`;

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<InternalUser | null>(null);

  const users = useQuery({
    queryKey: ['users', clientId],
    queryFn: () => internalFetch<{ users: InternalUser[] }>(base),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      environment: 'production',
      externalId: '',
      displayName: '',
      userCap: defaultUserCap ?? '',
      canEditOwnCap: false,
      canViewAmounts: true,
      active: true,
    },
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['users', clientId] });
    void qc.invalidateQueries({ queryKey: ['cap-changes', clientId] });
  };

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const displayName = values.displayName?.trim() === '' ? null : (values.displayName ?? null);
      const cap = values.userCap ?? '';
      if (editing === null) {
        return internalSend('POST', `${base}?environment=${values.environment}`, {
          externalId: values.externalId,
          displayName,
          // Empty => omit so the client default applies at creation.
          ...(cap === '' ? {} : { userCap: cap }),
          canEditOwnCap: values.canEditOwnCap,
          canViewAmounts: values.canViewAmounts,
        });
      }
      const path = `${base}/${encodeURIComponent(editing.externalId)}?environment=${editing.environment}`;
      return internalSend('PATCH', path, {
        displayName,
        active: values.active,
        // Empty => explicit "no cap" on edit.
        userCap: cap === '' ? null : cap,
        canEditOwnCap: values.canEditOwnCap,
        canViewAmounts: values.canViewAmounts,
      });
    },
    onSuccess: () => {
      closeModal();
      refresh();
      notify(editing === null ? 'Utilisateur ajouté' : 'Utilisateur mis à jour');
    },
  });

  const openCreate = (): void => {
    setEditing(null);
    form.reset({
      environment: 'production',
      externalId: '',
      displayName: '',
      userCap: defaultUserCap ?? '',
      canEditOwnCap: false,
      canViewAmounts: true,
      active: true,
    });
    setOpen(true);
  };

  const openEdit = (user: InternalUser): void => {
    setEditing(user);
    form.reset({
      environment: user.environment,
      externalId: user.externalId,
      displayName: user.displayName ?? '',
      userCap: user.userCap ?? '',
      canEditOwnCap: user.canEditOwnCap,
      canViewAmounts: user.canViewAmounts,
      active: user.active,
    });
    setOpen(true);
  };

  const closeModal = (): void => {
    setOpen(false);
    setEditing(null);
    save.reset();
    form.reset();
  };

  const rows = users.data?.users ?? [];
  const pagination = usePagination(rows, 25);
  const errors = form.formState.errors;

  return (
    <Card>
      <CardHead
        title="Utilisateurs"
        description={`Chaque utilisateur de l'ERP (en-tête X-Majuscule-User) avec son plafond mensuel et ses droits. Les ERP de production et de staging ont chacun leurs utilisateurs.${
          defaultUserCap !== null ? ` Plafond par défaut : ${formatEur(defaultUserCap)}.` : ''
        }`}
        className="pb-4"
        actions={
          <Button type="button" size="sm" onClick={openCreate}>
            <Plus aria-hidden="true" />
            Ajouter un utilisateur
          </Button>
        }
      />
      <Table className="min-w-[640px]">
        <TableHeader>
          <TableRow>
            <TableHead kind="name">Utilisateur</TableHead>
            <TableHead>Environnement</TableHead>
            <TableHead kind="num">Plafond / mois</TableHead>
            <TableHead>Droits</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead kind="act">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagination.pageItems.map((user) => (
            <TableRow key={`${user.environment}:${user.externalId}`}>
              <TableCell kind="name">
                <CellUser initials={initials(user.displayName ?? user.externalId)}>
                  <Cell2
                    primary={user.displayName ?? user.externalId}
                    secondary={user.externalId}
                    secondaryMono
                  />
                </CellUser>
              </TableCell>
              <TableCell>
                <EnvironmentBadge environment={user.environment} />
              </TableCell>
              <TableCell kind="num">
                {user.userCap === null ? '—' : formatEur(user.userCap)}
              </TableCell>
              <TableCell>
                <span className="flex flex-wrap gap-1.5">
                  {user.canEditOwnCap ? <Tag>Édite son plafond</Tag> : null}
                  <Tag>{user.canViewAmounts ? 'Voit les montants' : 'Pourcentage seul'}</Tag>
                </span>
              </TableCell>
              <TableCell>
                <Badge variant={user.active ? 'success' : 'neutral'}>
                  {user.active ? 'Actif' : 'Inactif'}
                </Badge>
              </TableCell>
              <TableCell kind="act" className="w-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  aria-label={`Modifier ${user.displayName ?? user.externalId}`}
                  onClick={() => {
                    openEdit(user);
                  }}
                >
                  Modifier
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {users.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
          ) : null}
          {rows.length === 0 && !users.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>
              Aucun utilisateur. Ajoutez les identifiants de l&apos;ERP.
            </TableEmptyRow>
          ) : null}
        </TableBody>
      </Table>
      <Pager itemLabel="utilisateurs" {...pagination.pagerProps} />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            closeModal();
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title={
              editing === null ? (
                'Nouvel utilisateur'
              ) : (
                <>
                  Modifier <span className="font-mono">{editing.externalId}</span>
                </>
              )
            }
            description="Plafond mensuel de l'utilisateur et droits associés."
          />
          <form
            id="user-form"
            onSubmit={(event) => {
              void form.handleSubmit((values) => {
                save.mutate(values);
              })(event);
            }}
            className="contents"
          >
            <DialogBody>
              <Fieldset title="Identité">
                <Field
                  label="Environnement"
                  hint="Un utilisateur Staging n'est visible qu'avec les clés Staging."
                >
                  <NativeSelect disabled={editing !== null} {...form.register('environment')}>
                    {environmentSchema.options.map((value) => (
                      <option key={value} value={value}>
                        {ENVIRONMENT_LABELS[value]}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Fields2>
                  <Field
                    label="Identifiant ERP (externalId)"
                    required
                    error={errors.externalId?.message}
                  >
                    <Input
                      className="font-mono"
                      placeholder="matricule-123"
                      disabled={editing !== null}
                      {...form.register('externalId')}
                    />
                  </Field>
                  <Field label="Nom affiché" error={errors.displayName?.message}>
                    <Input placeholder="Jean Dupont" {...form.register('displayName')} />
                  </Field>
                </Fields2>
              </Fieldset>
              <Fieldset title="Plafond et droits">
                <Field
                  label="Plafond"
                  hint={
                    editing === null
                      ? 'Vide = plafond par défaut du client.'
                      : 'Vide = aucun plafond.'
                  }
                  error={errors.userCap?.message}
                >
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      inputMode="decimal"
                      placeholder="100"
                      {...form.register('userCap')}
                    />
                  </InputGroup>
                </Field>
                <div className="grid gap-2">
                  <CheckRow
                    label="Peut modifier son propre plafond"
                    {...form.register('canEditOwnCap')}
                  />
                  <CheckRow
                    label="Voit les montants en euros (sinon pourcentage seul)"
                    {...form.register('canViewAmounts')}
                  />
                  {editing !== null ? (
                    <CheckRow label="Actif" {...form.register('active')} />
                  ) : null}
                </div>
              </Fieldset>
              {save.isError ? (
                <Callout variant="destructive">
                  Échec de l&apos;enregistrement (plafond supérieur au plafond client ou identifiant
                  déjà pris).
                </Callout>
              ) : null}
            </DialogBody>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button type="submit" form="user-form" size="lg" disabled={save.isPending}>
              {editing === null ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
